/**
 * SendComposer.jsx - Send Composition Zone Component
 * 
 * Checklist Items Covered:
 * - E1: Composition Container
 * - E2: Selected Template Display (Armed Message Card)
 * - E3: Recipient Count Badge
 * - E4: Recipient List Preview
 * - E5: Select All Recipients Button
 * - E6: Clear Selection Button
 * - E7: Schedule Send Toggle
 * - E8: Schedule DateTime Picker
 * - E9: Send Now Button
 * - E10: Send Progress Indicator
 */

import React, { useState, useEffect } from 'react';
import { 
  Mail, MailOpen, Eye, Zap, Users, UserPlus, UserMinus, Calendar, 
  Clock, Send, Loader2, CheckCircle, AlertCircle, X
} from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // E1: Composition Container
  composerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },

  // E2: Armed Message Card
  armedMessageCard: {
    position: 'relative',
    padding: 20,
    borderRadius: 18,
    border: '1px solid rgba(198,167,90,0.22)',
    overflow: 'hidden',
  },
  armedMessageGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(198,167,90,0.16) 0%, rgba(198,167,90,0.08) 50%, rgba(255,255,255,0.00) 100%)',
    pointerEvents: 'none',
  },
  armedMessageEmpty: {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.00) 100%)',
  },
  armedTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
    position: 'relative',
    zIndex: 1,
  },
  armedEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: COLORS.goldDark,
    marginBottom: 4,
  },
  armedName: {
    fontSize: 17,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 6,
    letterSpacing: '-0.2px',
  },
  armedSubjectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  armedSubject: {
    fontSize: 13,
    color: COLORS.textMuted,
  },
  armedPreviewBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  armedPreviewText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
  
  // Metrics Row
  metricsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: '14px 0',
    borderTop: `1px solid ${COLORS.borderLight}`,
    marginTop: 8,
    position: 'relative',
    zIndex: 1,
  },
  metricDivider: {
    width: 1,
    height: 32,
    background: COLORS.borderLight,
    margin: '0 16px',
  },
  metric: {
    flex: 1,
    textAlign: 'center',
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },

  // Silk Slide Band (send ready indicator)
  silkSlideBand: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    marginTop: 12,
    borderRadius: 10,
    background: 'linear-gradient(90deg, rgba(198,167,90,0.16) 0%, rgba(198,167,90,0.08) 50%, rgba(255,255,255,0.00) 100%)',
    position: 'relative',
    zIndex: 1,
  },
  silkSlideText: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.goldDark,
  },

  // Empty State
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  emptyIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 6,
  },
  emptyBody: {
    fontSize: 13,
    color: COLORS.textMuted,
    maxWidth: 260,
    lineHeight: 1.5,
  },

  // E3-E6: Recipient Selection Card
  recipientCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
  },
  recipientHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  recipientTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  recipientBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    background: COLORS.goldLight,
  },
  recipientBadgeText: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.goldDark,
  },
  recipientActions: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  recipientActionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
  recipientList: {
    maxHeight: 200,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  recipientItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    background: '#FFFCF7',
    border: `1px solid ${COLORS.borderLight}`,
  },
  recipientItemSelected: {
    background: COLORS.goldLight,
    borderColor: COLORS.gold,
  },
  recipientAvatar: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #E8DFD0 0%, #D4C9B8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textSecondary,
  },
  recipientName: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  recipientEmail: {
    fontSize: 11,
    color: COLORS.textMuted,
  },
  recipientCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: `2px solid ${COLORS.borderMedium}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  recipientCheckboxSelected: {
    background: COLORS.gold,
    borderColor: COLORS.gold,
  },
  recipientEmpty: {
    padding: 24,
    textAlign: 'center',
  },
  recipientEmptyText: {
    fontSize: 13,
    color: COLORS.textMuted,
  },

  // E7-E8: Schedule Section
  scheduleCard: {
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
  },
  scheduleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  scheduleTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  scheduleToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    background: '#E4DBC9',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 0.2s',
  },
  scheduleToggleActive: {
    background: COLORS.gold,
  },
  scheduleToggleKnob: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 20,
    height: 20,
    borderRadius: 10,
    background: '#FFFFFF',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
  },
  scheduleToggleKnobActive: {
    transform: 'translateX(20px)',
  },
  schedulePicker: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
  },
  scheduleInput: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    color: COLORS.textPrimary,
  },

  // E9-E10: Send Button
  sendButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sendButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '16px 24px',
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 6px 16px rgba(198,167,90,0.3)',
  },
  sendButtonDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  sendButtonText: {
    fontSize: 15,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  sendButtonTextDisabled: {
    color: '#A89C82',
  },
  sendProgress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '12px',
  },
  sendProgressText: {
    fontSize: 13,
    color: COLORS.gold,
    fontWeight: 600,
  },
};

// ─── Component ──────────────────────────────────────────────────────────────────

const SendComposer = ({
  selectedTemplate,
  guests = [],
  selectedGuests = [],
  onToggleGuest,
  onSelectAll,
  onClearAll,
  onPreviewTemplate,
  onSend,
  isSending,
  deliveryRate = '—',
  openRate = '—',
}) => {
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const isReadyToSend = selectedTemplate && selectedGuests.length > 0;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleSend = () => {
    if (!isReadyToSend || isSending) return;
    
    const scheduleDateTime = scheduleEnabled && scheduleDate && scheduleTime
      ? `${scheduleDate}T${scheduleTime}`
      : null;
    
    onSend?.(selectedTemplate.id, selectedGuests, scheduleDateTime);
  };

  return (
    <div style={styles.composerContainer} data-testid="send-composer">
      
      {/* E2: Armed Message Card */}
      <div 
        style={styles.armedMessageCard}
        data-testid="armed-message-card"
      >
        <div style={{
          ...styles.armedMessageGradient,
          ...(selectedTemplate ? {} : styles.armedMessageEmpty),
        }} />
        
        {selectedTemplate ? (
          <>
            <div style={styles.armedTopRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.armedEyebrow}>Armed Message</div>
                <div style={styles.armedName}>{selectedTemplate.name}</div>
                <div style={styles.armedSubjectRow}>
                  <Mail size={11} color={COLORS.goldDark} />
                  <span style={styles.armedSubject}>
                    {selectedTemplate.subject || 'No subject'}
                  </span>
                </div>
              </div>
              <button
                style={styles.armedPreviewBtn}
                onClick={() => onPreviewTemplate?.(selectedTemplate)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                  e.currentTarget.style.borderColor = COLORS.gold;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
                  e.currentTarget.style.borderColor = COLORS.borderMedium;
                }}
                data-testid="armed-preview-btn"
              >
                <Eye size={13} color={COLORS.goldDark} />
                <span style={styles.armedPreviewText}>Preview</span>
              </button>
            </div>
            
            {/* Metrics Row */}
            <div style={styles.metricsRow}>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Delivery Rate</div>
                <div style={styles.metricValue}>{deliveryRate}</div>
              </div>
              <div style={styles.metricDivider} />
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Open Rate</div>
                <div style={styles.metricValue}>{openRate}</div>
              </div>
              <div style={styles.metricDivider} />
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Recipients</div>
                <div style={styles.metricValue}>
                  {selectedGuests.length === 0 ? '—' : selectedGuests.length}
                </div>
              </div>
            </div>

            {/* Silk Slide Band - appears when ready */}
            {isReadyToSend && (
              <div style={styles.silkSlideBand} data-testid="silk-slide-band">
                <Zap size={13} color={COLORS.gold} />
                <span style={styles.silkSlideText}>
                  Send path armed — ready to send to {selectedGuests.length} recipient{selectedGuests.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </>
        ) : (
          <div style={styles.emptyStateContainer}>
            <div style={styles.emptyIconWrap}>
              <MailOpen size={22} color={COLORS.gold} />
            </div>
            <div style={styles.emptyTitle}>No Message Armed</div>
            <div style={styles.emptyBody}>
              Select a template from the staging column to arm this send path.
            </div>
          </div>
        )}
      </div>

      {/* E3-E6: Recipient Selection */}
      <div style={styles.recipientCard} data-testid="recipient-card">
        <div style={styles.recipientHeader}>
          <span style={styles.recipientTitle}>Recipient Selection</span>
          {/* E3: Recipient Count Badge */}
          <div style={styles.recipientBadge} data-testid="recipient-badge">
            <Users size={12} color={COLORS.goldDark} />
            <span style={styles.recipientBadgeText}>
              {selectedGuests.length} of {guests.length} selected
            </span>
          </div>
        </div>
        
        {/* E5-E6: Select All / Clear Buttons */}
        <div style={styles.recipientActions}>
          <button
            style={styles.recipientActionBtn}
            onClick={onSelectAll}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = COLORS.borderMedium;
            }}
            data-testid="select-all-btn"
          >
            <UserPlus size={14} />
            Select All
          </button>
          <button
            style={styles.recipientActionBtn}
            onClick={onClearAll}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = COLORS.borderMedium;
            }}
            data-testid="clear-all-btn"
          >
            <UserMinus size={14} />
            Clear
          </button>
        </div>

        {/* E4: Recipient List */}
        <div style={styles.recipientList} data-testid="recipient-list">
          {guests.length === 0 ? (
            <div style={styles.recipientEmpty}>
              <p style={styles.recipientEmptyText}>No guests available</p>
            </div>
          ) : (
            guests.map((guest) => {
              const isSelected = selectedGuests.includes(guest.id);
              const displayName = `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Unknown Guest';
              return (
                <div
                  key={guest.id}
                  style={{
                    ...styles.recipientItem,
                    ...(isSelected ? styles.recipientItemSelected : {}),
                  }}
                  onClick={() => onToggleGuest?.(guest.id)}
                  data-testid={`recipient-${guest.id}`}
                >
                  <div style={styles.recipientAvatar}>
                    {getInitials(displayName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.recipientName}>{displayName}</div>
                    <div style={styles.recipientEmail}>{guest.email || 'No email'}</div>
                  </div>
                  <div
                    style={{
                      ...styles.recipientCheckbox,
                      ...(isSelected ? styles.recipientCheckboxSelected : {}),
                    }}
                  >
                    {isSelected && <CheckCircle size={12} color="#fff" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* E7-E8: Schedule Section */}
      <div style={styles.scheduleCard} data-testid="schedule-card">
        <div style={styles.scheduleHeader}>
          <div style={styles.scheduleTitle}>
            <Calendar size={16} color={COLORS.goldDark} />
            Schedule Send
          </div>
          {/* E7: Schedule Toggle */}
          <div
            style={{
              ...styles.scheduleToggle,
              ...(scheduleEnabled ? styles.scheduleToggleActive : {}),
            }}
            onClick={() => setScheduleEnabled(!scheduleEnabled)}
            data-testid="schedule-toggle"
          >
            <div
              style={{
                ...styles.scheduleToggleKnob,
                ...(scheduleEnabled ? styles.scheduleToggleKnobActive : {}),
              }}
            />
          </div>
        </div>
        
        {/* E8: Schedule DateTime Picker */}
        {scheduleEnabled && (
          <div style={styles.schedulePicker}>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              style={styles.scheduleInput}
              data-testid="schedule-date"
            />
            <input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              style={styles.scheduleInput}
              data-testid="schedule-time"
            />
          </div>
        )}
      </div>

      {/* E9-E10: Send Button */}
      <div style={styles.sendButtonContainer}>
        {/* E10: Send Progress */}
        {isSending && (
          <div style={styles.sendProgress} data-testid="send-progress">
            <Loader2 size={18} color={COLORS.gold} style={{ animation: 'spin 1s linear infinite' }} />
            <span style={styles.sendProgressText}>Sending to {selectedGuests.length} recipients...</span>
          </div>
        )}
        
        {/* E9: Send Button */}
        <button
          style={{
            ...styles.sendButton,
            ...(!isReadyToSend || isSending ? styles.sendButtonDisabled : {}),
          }}
          onClick={handleSend}
          disabled={!isReadyToSend || isSending}
          onMouseEnter={(e) => {
            if (isReadyToSend && !isSending) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(198,167,90,0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.3)';
          }}
          data-testid="send-button"
        >
          {isSending ? (
            <Loader2 size={18} color="#A89C82" />
          ) : (
            <Send size={18} color={isReadyToSend ? '#fff' : '#A89C82'} />
          )}
          <span style={{
            ...styles.sendButtonText,
            ...(!isReadyToSend || isSending ? styles.sendButtonTextDisabled : {}),
          }}>
            {scheduleEnabled ? 'Schedule Send' : 'Send Now'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default SendComposer;
